import React from "react";
import { motion } from "framer-motion";
import { Overline, KolamGrid } from "./Motifs";

const JOURNEY1 =
  "https://customer-assets.emergentagent.com/job_meera-adventures/artifacts/8x9pnoah_ChatGPT%20Image%20Jun%209%2C%202026%2C%2011_08_05%20PM.png";
const JOURNEY2 =
  "https://customer-assets.emergentagent.com/job_meera-adventures/artifacts/rn0e82hf_ChatGPT%20Image%20Jun%2010%2C%202026%2C%2009_14_27%20PM.png";

const MILESTONES = [
  { year: "2011", text: "Master's in Design Thinking, Domus Academy, Milan" },
  { year: "2011–2016", text: "Taught at Srishti Institute of Art, Design & Technology" },
  { year: "2013–2017", text: "The Happy Wall Project — community art across Bengaluru" },
  { year: "2017–2018", text: "Indus International School — creative methodologies for Math & Science" },
  { year: "2018–2020", text: "BYJU'S — character design, environments, voiceovers" },
  { year: "2020–2022", text: "Embibe — curriculum & content design" },
  { year: "2020–2025", text: "PhD at SSBM Geneva — AR technology for special-needs learners" },
  { year: "2022 → now", text: "Founder, Magic Ink — children's storytelling & audiobook platform" },
  { year: "Aug 2026", text: "Atta Galatta Poetry Festival — featured author for Meera and the Magic Kolam" },
  { year: "Dec 2026", text: "Bangalore Literature Festival — featured author for Meera and the Magic Kolam" },
];

export default function Journey() {
  return (
    <section
      id="journey"
      data-testid="journey-section"
      className="relative py-24 sm:py-32 overflow-hidden"
    >
      <div className="absolute -top-12 -left-16 w-72 h-72 text-[#D95D39] opacity-10 pointer-events-none kolam-bg">
        <KolamGrid cols={9} rows={9} className="w-full h-full" />
      </div>

      <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16 relative">
        <div className="grid lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-7">
            <Overline>15 Years of Following Curiosity</Overline>
            <h2 className="font-display text-5xl sm:text-6xl lg:text-7xl text-[#1E2A4F] mt-5 leading-[0.95] tracking-tight">
              The path here.
              <br />
              <em className="italic font-light">Different roads, one purpose.</em>
            </h2>
          </div>
          <div className="lg:col-span-5 text-[#5C6A8F] leading-relaxed">
            Designer, educator, author, artist, researcher, storyteller. A wandering
            CV that — looked at sideways — turns out to be one long love letter to
            creativity, learning, and human connection.
          </div>
        </div>

        {/* Two infographics */}
        <div className="mt-16 grid lg:grid-cols-2 gap-8">
          <motion.figure
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9 }}
            className="overflow-hidden rounded-lg border border-[#E5DFD3] bg-white"
            data-testid="journey-infographic-1"
          >
            <img
              src={JOURNEY1}
              alt="15 Years of Following Curiosity — the many adventures of Dr. Divya Ramachandran"
              className="w-full h-auto block"
              loading="lazy"
            />
            <figcaption className="px-6 py-4 text-xs tracking-[0.22em] uppercase text-[#5C6A8F] border-t border-[#E5DFD3]">
              15 years, mapped by hand
            </figcaption>
          </motion.figure>

          <motion.figure
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9, delay: 0.15 }}
            className="overflow-hidden rounded-lg border border-[#E5DFD3] bg-white"
            data-testid="journey-infographic-2"
          >
            <img
              src={JOURNEY2}
              alt="Divya Ramachandran — My Journey So Far. Designer, Educator, Storyteller, Playmaker."
              className="w-full h-auto block"
              loading="lazy"
            />
            <figcaption className="px-6 py-4 text-xs tracking-[0.22em] uppercase text-[#5C6A8F] border-t border-[#E5DFD3]">
              Designer • educator • storyteller • playmaker
            </figcaption>
          </motion.figure>
        </div>

        {/* Text timeline */}
        <div className="mt-20 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-4">
            <Overline>The short version</Overline>
            <h3 className="font-display text-3xl sm:text-4xl text-[#1E2A4F] mt-4 leading-tight">
              A clean timeline,
              <br />
              <em className="italic font-light">if you'd rather read.</em>
            </h3>
          </div>
          <ul className="lg:col-span-8 divide-y divide-[#E5DFD3] border-y border-[#E5DFD3]">
            {MILESTONES.map((m, i) => (
              <li
                key={`${m.year}-${i}`}
                data-testid={`milestone-${i}`}
                className="grid grid-cols-12 gap-4 py-5 items-baseline"
              >
                <span className="col-span-12 sm:col-span-3 font-display text-[#D95D39] text-base tabular-nums tracking-wide">
                  {m.year}
                </span>
                <span className="col-span-12 sm:col-span-9 text-[#1E2A4F] text-[15px] leading-relaxed">
                  {m.text}
                </span>
              </li>
            ))}
          </ul>
        </div>

        {/* North star quote */}
        <motion.blockquote
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.9 }}
          className="mt-20 p-10 sm:p-16 rounded-lg bg-[#1E2A4F] text-[#F9F6F0] relative overflow-hidden"
          data-testid="journey-north-star"
        >
          <div className="absolute -top-16 -right-16 w-72 h-72 rounded-full bg-[#D95D39]/20 blur-3xl" />
          <Overline className="!text-[#E76F51] relative">My north star</Overline>
          <p className="relative font-display italic text-3xl sm:text-4xl lg:text-5xl mt-6 leading-tight max-w-4xl">
            &ldquo;To use creativity, stories, and learning to make the world a kinder,
            more curious, more connected place.&rdquo;
          </p>
        </motion.blockquote>
      </div>
    </section>
  );
}
